import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, languages } from '../lib/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language or default to English
    const saved = localStorage.getItem('language');
    return saved && translations[saved] ? saved : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Update HTML lang attribute
    document.documentElement.lang = language;
  }, [language]);

  // Translation function
  const t = (key, fallback = null) => {
    const translation = translations[language]?.[key];
    if (translation) return translation;
    
    // Fallback to English if key not found in current language
    const englishTranslation = translations['en']?.[key];
    if (englishTranslation) return englishTranslation;
    
    // Return fallback or key itself
    return fallback || key;
  };

  // Change language
  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setLanguage(langCode);
    }
  };

  // Get current language info
  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage: changeLanguage,
      t,
      languages,
      currentLanguage,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
